
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { MetricCard } from '../components/MetricCard.tsx';
import { Link } from 'react-router-dom';
import {
  Activity,
  Calendar,
  Zap,
  ArrowRight,
  ShieldAlert,
  Clock,
  Sparkles,
  Loader2,
  Users,
  DollarSign,
  TrendingDown,
  AlertTriangle
} from 'lucide-react';
import { formatIndex, formatCompactCurrency, formatCurrency } from '../services/financialService.ts';
import { refineRiskMitigation, refineIssueResolution } from '../services/geminiService.ts';
import { useToast } from '../hooks/useToast.ts';

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const projects = useStore(() => api.projects.list()) || [];
  const issues = useStore(() => api.issues.list()) || [];
  const risks = useStore(() => api.risks.list(), [], 'risks') || [];
  const subcontractors = useStore(() => api.subcontractors.list(), [], 'subcontractors') || [];
  const evmSnapshots = useStore(() => api.evm.list()) || [];
  const changeOrders = useStore(() => api.changeOrders.list()) || [];
  const compliance = useStore(() => api.complianceRequirements.list(), [], 'complianceRequirements') || [];
  const { addToast } = useToast();

  const [hurdleTab, setHurdleTab] = useState<'risks' | 'issues'>('risks');
  const [refiningId, setRefiningId] = useState<number | null>(null);

  const stats = useMemo(() => {
    const active = projects.filter(p => p.status === 'Active');
    const totalMagnitude = active.reduce((acc, p) => acc + p.contractAmount, 0);
    const pendingCOs = changeOrders.filter(co => co.status !== 'Approved');
    const unbilledExposure = pendingCOs.reduce((acc, co) => acc + co.amount, 0);

    let totalCv = 0;
    active.forEach(p => {
      const snapshots = evmSnapshots.filter(s => s.projectId === p.id);
      if (snapshots.length > 0) {
        const last = snapshots[snapshots.length - 1];
        totalCv += (last.earnedValue - last.actualCost);
      }
    });

    const criticalSubs = subcontractors.filter(s => s.scheduleStatus === 'Critical' || s.scheduleStatus === 'Behind').length;
    const marginIndex = totalMagnitude > 0 ? (totalMagnitude + totalCv) / totalMagnitude : 1.0;

    return { totalMagnitude, unbilledExposure, totalCv, marginIndex, criticalSubs };
  }, [projects, evmSnapshots, changeOrders, subcontractors]);

  const handleRefineHurdle = async (item: any) => {
    if (!item.id) return;
    setRefiningId(item.id);
    try {
      const project = projects.find(p => p.id === item.projectId);
      const title = project?.title || t('common.unknown_job');

      if (hurdleTab === 'risks') {
        const refined = await refineRiskMitigation(title, item.summary, item.mitigationPlan);
        await api.risks.update(item.id, { mitigationPlan: refined });
      } else {
        const refined = await refineIssueResolution(title, item.description, item.priority);
        await api.issues.update(item.id, { description: item.description + "\n\nFIELD DIRECTIVE:\n" + refined });
      }
      addToast({ type: 'success', title: t('common.directive_refined'), message: t('common.strategy_updated') });
    } catch (err) {
      addToast({ type: 'error', title: t('common.refinement_failed'), message: t('common.intelligence_timeout') });
    } finally {
      setRefiningId(null);
    }
  };

  const deadlineWatch = useMemo(() => {
    const now = new Date().getTime();
    return compliance
      .filter(c => c.status !== 'Completed')
      .map(c => {
        const due = new Date(c.dueDate).getTime();
        const daysLeft = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
        return { ...c, daysLeft };
      })
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .slice(0, 5);
  }, [compliance]);

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-1000">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-zinc-900 text-zinc-100 text-[9px] font-black uppercase tracking-[0.2em] rounded-lg shadow-xl">
            <ShieldAlert size={12} className="text-amber-500 fill-amber-500" /> {t('dashboard.contractor_profit_center')}
          </div>
          <h1 className="text-2xl font-black text-black tracking-tight leading-none uppercase">
            {t('dashboard.margin_command_center')} & Intelligence
          </h1>
          <p className="text-zinc-500 font-bold text-sm max-w-xl">
            {t('dashboard.dashboard_desc')}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-black uppercase text-zinc-300 tracking-[0.3em] mb-2">{t('dashboard.system_cycle')}</div>
          <div className="flex items-center gap-3 font-black text-black text-lg uppercase tracking-tight">
            <Clock size={18} className="text-zinc-300" /> {new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <MetricCard label={t('dashboard.total_job_magnitude')} value={formatCompactCurrency(stats.totalMagnitude)} variant="dark" />
        <MetricCard label={t('dashboard.margin_integrity')} value={formatIndex(stats.marginIndex)} status={stats.marginIndex < 1.0 ? 'danger' : 'success'} subValue={stats.marginIndex < 1.0 ? t('dashboard.margin_bleed') : t('dashboard.profit_maintained')} />
        <MetricCard label={t('dashboard.unbilled_exposure')} value={formatCompactCurrency(stats.unbilledExposure)} status={stats.unbilledExposure > 50000 ? 'warning' : 'neutral'} subValue={t('dashboard.potential_co_value')} />
        <MetricCard label={t('dashboard.cost_variance_total')} value={formatCompactCurrency(Math.abs(stats.totalCv))} status={stats.totalCv < 0 ? 'danger' : 'success'} subValue={stats.totalCv < 0 ? t('dashboard.aggregate_loss') : t('dashboard.aggregate_savings')} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-12">
        <div className="xl:col-span-3 space-y-12">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-8 rounded-[2.5rem] border border-zinc-200 flex items-center gap-6 shadow-sm group">
              <div className="p-4 bg-zinc-50 rounded-2xl text-black shadow-inner group-hover:bg-amber-500 group-hover:text-white transition-all"><TrendingDown size={24} /></div>
              <div>
                <h4 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mb-1">{t('dashboard.trade_bottlenecks')}</h4>
                <span className="text-2xl font-black">{stats.criticalSubs} <span className="text-[10px] text-zinc-300 ml-1">{t('common.firms')}</span></span>
              </div>
            </div>
            <div className="bg-white p-8 rounded-[2.5rem] border border-zinc-200 flex items-center gap-6 shadow-sm group">
              <div className="p-4 bg-zinc-50 rounded-2xl text-black shadow-inner group-hover:bg-emerald-500 group-hover:text-white transition-all"><DollarSign size={24} /></div>
              <div>
                <h4 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mb-1">{t('dashboard.open_claims')}</h4>
                <span className="text-2xl font-black">{changeOrders.filter(c => c.status === 'Submitted').length} <span className="text-[10px] text-zinc-300 ml-1">{t('common.submitted')}</span></span>
              </div>
            </div>
            <div className="bg-white p-8 rounded-[2.5rem] border border-zinc-200 flex items-center gap-6 shadow-sm group">
              <div className="p-4 bg-zinc-50 rounded-2xl text-black shadow-inner group-hover:bg-black group-hover:text-white transition-all"><Activity size={24} /></div>
              <div>
                <h4 className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mb-1">{t('dashboard.compliance_gaps')}</h4>
                <span className="text-2xl font-black">{compliance.filter(c => c.status !== 'Completed').length} <span className="text-[10px] text-zinc-300 ml-1">{t('common.pending')}</span></span>
              </div>
            </div>
          </div>

          <div className="bg-black rounded-[3.5rem] border border-zinc-800 p-12 text-white relative overflow-hidden shadow-2xl ai-shimmer group">
            <div className="relative z-10">
              <h3 className="text-2xl font-black tracking-tighter uppercase flex items-center gap-4 mb-10">
                <AlertTriangle size={28} className="text-amber-500" /> {t('dashboard.critical_margin_alerts')}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {deadlineWatch.length > 0 ? deadlineWatch.map((item, idx) => (
                  <div key={item.id} style={{ animationDelay: `${idx * 100}ms` }} className={`p-6 rounded-[2rem] border transition-all duration-500 hover:scale-[1.02] animate-in fade-in zoom-in-95 ${item.daysLeft < 3 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.2em] ${item.daysLeft < 3 ? 'bg-amber-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                        {item.daysLeft <= 0 ? t('common.expired') : `${item.daysLeft}${t('common.days_remaining')}`}
                      </span>
                      <span className="text-[9px] text-zinc-700 font-black uppercase tracking-widest">{t('common.regulatory')}</span>
                    </div>
                    <h4 className="text-lg font-black text-white leading-tight mb-5 truncate">{item.title}</h4>
                    <Link to={`/projects/${item.projectId}`} className="text-zinc-600 hover:text-white transition-colors flex items-center gap-3 text-[10px] font-black uppercase tracking-widest">
                      {t('common.go_to_command')} <ArrowRight size={12} />
                    </Link>
                  </div>
                )) : (
                  <p className="text-zinc-500 font-black uppercase text-[10px] tracking-widest py-10">{t('dashboard.all_compliant')}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-[3.5rem] border border-zinc-200 flex flex-col h-full shadow-sm">
          <div className="px-12 py-8 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <ShieldAlert size={24} className="text-amber-600" />
              <h3 className="font-black text-xl text-black uppercase tracking-tight">{t('dashboard.hurdle_audit')}</h3>
            </div>
            <div className="flex p-1.5 bg-zinc-100 rounded-xl">
              <button onClick={() => setHurdleTab('risks')} className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[0.2em] rounded-lg transition-all ${hurdleTab === 'risks' ? 'bg-white text-black shadow-lg' : 'text-zinc-400'}`}>{t('common.risks')}</button>
              <button onClick={() => setHurdleTab('issues')} className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[0.2em] rounded-lg transition-all ${hurdleTab === 'issues' ? 'bg-white text-black shadow-lg' : 'text-zinc-400'}`}>{t('common.field')}</button>
            </div>
          </div>
          <div className="p-8 space-y-6 overflow-y-auto max-h-[850px] custom-scrollbar">
            {hurdleTab === 'risks' ? (
              risks.filter(r => r.level === 'High' || r.level === 'Critical').map(item => (
                <div key={item.id} className="p-6 rounded-[2rem] border border-zinc-100 bg-zinc-50/30 hover:bg-white hover:border-zinc-200 hover:shadow-xl transition-all duration-500 group">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="text-sm font-black text-black leading-tight pr-6">{item.summary}</h4>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => handleRefineHurdle(item)} disabled={refiningId === item.id} className={`p-2 rounded-xl border transition-all ${refiningId === item.id ? 'bg-black text-white' : 'text-zinc-300 border-zinc-100 hover:text-black hover:border-black'}`}>
                        {refiningId === item.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      </button>
                      <span className="px-2.5 py-1 bg-amber-500 text-white text-[8px] font-black uppercase tracking-[0.2em] rounded-lg h-fit shadow-lg">{item.level}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              issues.filter(i => !i.isResolved).map(item => (
                <div key={item.id} className="p-6 rounded-[2rem] border border-zinc-100 bg-zinc-50/30 hover:bg-white hover:border-zinc-200 hover:shadow-xl transition-all duration-500 group">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-sm font-black text-black leading-tight pr-6">
                      {item.description?.split('\n')[0] || t('common.no_description')}
                    </h4>
                    <span className={`px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.2em] rounded-lg h-fit shadow-lg ${item.priority === 'High' ? 'bg-amber-600 text-white' : 'bg-amber-500 text-white'}`}>{item.priority}</span>
                  </div>
                  <button onClick={() => handleRefineHurdle(item)} disabled={refiningId === item.id} className="w-full mt-2 py-2 text-[8px] font-black uppercase tracking-widest text-zinc-400 hover:text-black border border-dashed border-zinc-200 rounded-xl">
                    {t('dashboard.ai_recovery_logic')}
                  </button>
                </div>
              ))
            )}
            {((hurdleTab === 'risks' && risks.length === 0) || (hurdleTab === 'issues' && issues.length === 0)) && (
              <div className="py-20 text-center text-zinc-300 font-black uppercase tracking-widest text-[10px]">{t('common.registry_nominal')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
